import React from "react";

import BaseClass from "../../util/BaseClass";
import Context from "../../context/Context";
import HostedMerge from "./HostedMerge";
import Issues from "../components/common/Issues";
import {ModalController, WorkingController} from "../../common/components/Modal";
import OfficePlan from "./OfficePlan";
import PlanOpener from "./PlanOpener";
import Project from "./Project";
import Topic from "../../context/Topic";
import * as mergeUtil from "./mergeUtil";
import * as sourceUtil from "./sourceUtil";
import * as val from "../../util/val";
import * as placeholderUtil from '../base/placeholderUtil';
import { getPlaceholdersToReplace, makeReplaceRequest } from "./replacePlaceholders";
import { ReplacePlaceholder } from "./ReplacePlaceholder";

export default class Planner extends BaseClass {

  project: Project;

  static getParentAppId(planItem) {
    let appId = null;
    if (planItem.versionInfo) {
      const appItem = Context.instance.configuration.appItem;
      return appItem && appItem.id;
    } else {
      const pfx = OfficePlan.TypeKeywordParentPfx;
      const a = planItem && planItem.typeKeywords;
      if (Array.isArray(a)) {
        a.some(v => {
          if (v && v.indexOf(pfx) === 0) {
            appId = v.substring(pfx.length);
          }
          return (!!appId);
        });
      }
    }
    return appId;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
  async _checkReconcileOnOpen(project,plan) {
    let defaultHasChanges = false;
    const vm = project.versionedInfo.versionManager;
    const isOwner = vm && vm.isOwner(plan.versionInfo);
    const ancestorDate = plan.versionInfo.commonAncestorDate;
    const guid = vm && vm.serviceInfo && vm.serviceInfo.defaultVersionGuid;
    const infos = vm && vm.versionInfos;
    if (isOwner && guid && infos) {
      infos.some(info => {
        if (info.versionGuid === guid) {
          const modDate = info.modifiedDate;
          if (modDate > ancestorDate) {
            defaultHasChanges = true;
          }
          return true;
        }
        return false;
      })
    }
    if (defaultHasChanges) {
      const i18n = Context.instance.i18n;
      const modalOptions = {
        className: "i-modal-confirm i-modal-small",
        title: i18n.general.information,
        message: i18n.spaceplanner.backstage.mergePlan.reconcilePromptAlt,
        okLabel: i18n.general.yes,
        cancelLabel: i18n.more.bookWorkspace.no,
        closeOnCancel: true
      }
      const options = {
        plan: plan,
        versionManager: vm,
        reconcile: true,
        applyEdits: true,
        post: false,
        cancelMerge: false
      };
      ModalController.confirm(modalOptions).then(async (result) => {
        if (result.ok) {
          const workingController = new WorkingController();
          workingController.show({}, i18n.spaceplanner.backstage.mergePlan.replacePlaceholders.controller);
          try {
            await placeholderUtil.checkReplacePlaceholders(options, workingController);
            if (options.cancelMerge) return;
          } catch (error) {
            workingController.close();
            console.error('Error searching placeholders', error);            
          }
          const workingContent = i18n.spaceplanner.backstage.mergePlan.gettingLatest;
          workingController.show({}, workingContent);
          mergeUtil.analyze(options).then(task => {
            //console.log("MergePlan mergeUtil::analyze",task)
            workingController.close();
          }).catch(ex => {
            let submessage = ex.submessage || ex.message;
            workingController.close();
            ModalController.alert({
              isError: true,
              title: i18n.spaceplanner.backstage.mergePlan.errorMergingTitle,
              message: i18n.spaceplanner.backstage.mergePlan.errorMergingMessage,
              submessage: submessage
            });
            console.error("Error merging plan", ex);
          });
        }
      })
    }
  }

  _checkReconcileOnOpenHosted(project: Project, plan: OfficePlan) {
    const isOwner = Context.instance.user.isPlanOwner();
    if (!isOwner) return;
    let hm = new HostedMerge();
    hm.defaultHasChanges().then(result => {
      const defaultHasChanges = (result && result.hasChanges);
      if (defaultHasChanges) {
        const i18n = Context.instance.i18n;
        const modalOptions = {
          className: "i-modal-confirm i-modal-small",
          title: i18n.general.information,
          message: i18n.spaceplanner.backstage.mergePlan.reconcilePromptAlt,
          okLabel: i18n.general.yes,
          cancelLabel: i18n.more.bookWorkspace.no,
          closeOnCancel: true
        }
        ModalController.confirm(modalOptions).then(async (result) => {
          if (result.ok) {
            const options = {
              plan: plan,
              reconcileOnly: true,
              cancelMerge: false
            };
            const workingController = new WorkingController();
            workingController.show({}, i18n.spaceplanner.backstage.mergePlan.replacePlaceholders.controller);
            try {
              await placeholderUtil.checkReplacePlaceholders(options, workingController);
              if (options.cancelMerge) return;
            } catch (error) {
              workingController.close()
              console.error(error);
            }
            const workingContent = i18n.spaceplanner.backstage.mergePlan.gettingLatest;
            workingController.show({}, workingContent);
            hm = new HostedMerge();
            hm.merge(options).then(task => {
              //console.log("MergePlan mergeUtil::analyze",task)
              workingController.close();
            }).catch(ex => {
              let submessage = ex.submessage || ex.message;
              workingController.close();
              ModalController.alert({
                isError: true,
                title: i18n.spaceplanner.backstage.mergePlan.errorMergingTitle,
                message: i18n.spaceplanner.backstage.mergePlan.errorMergingMessage,
                submessage: submessage
              });
              console.error("Error merging plan", ex);
            });
          }
        })
      }
    }).catch(ex => {
      console.error(ex);
    })
  }

  _handleOpenPlanError(task, project: Project, error) {
    this._showOpenPlan(task,project);
    const errorValues = Object.values(error);
    let isPlanInvalid;
    let message = error.message;
    let submessage = error.submessage;
    if(errorValues && errorValues.length >0)
    {
      if(errorValues[0] === "__invalidPlanId__") isPlanInvalid = true;
    }
    if(error.name === "identity-manager:not-authorized" || isPlanInvalid){
      const username=Context.instance.user.getUsername();
      submessage=null;
      message=Context.instance.i18n.spaceplanner.issues.noAccessToPlan;
      message=message.replace("{username}", username);
    }
    ModalController.confirm({
      isError: true,
      title: Context.instance.i18n.spaceplanner.issues.unableToOpenPlanTitle,
      message: message,
      submessage: submessage,
      hideCancel:true
    });
  }

  _handleOpenProjectError(task, project: Project, error) {
    const i18n = Context.instance.i18n;
    let message = error.message;
    let submessage = error.submessage;
    if (message.includes("Server object extension 'versionmanagementserver' not found")) {
      message = i18n.misc.noVersionManager;
    }
    ModalController.confirm({
      isError: true,
      title: i18n.spaceplanner.issues.unableToOpenProjectTitle,
      message: message,
      submessage: submessage
    }).then(() => {
      this._showOpenPlan(task,project);
    });
  }

  hasValidPlan() {
    const plan = Context.instance.spaceplanner.activePlan;
    return !!(plan && plan.wasOpened && !plan.hasIssues());
  }

  hasValidProject() {
    return !!(this.project && !this.project.hasIssues());
  }

  isBaselinePeopleLayerValid() {
    const source = sourceUtil.getInitialPeopleSource();
    return !!(source && source.layer2D);
  }

  isBaselineUnitsLayerValid() {
    const source = sourceUtil.getInitialUnitsSource();
    return !!(source && source.layer2D);
  }

  load() {
    const promise = new Promise<void>((resolve,reject) => {
      const lib = Context.instance.lib;
      let planId = Context.instance.spaceplanner.planId
      if (!planId) {
        const url = lib.esri.urlUtils.urlToObject(window.location.href);
        if (url && url.query) {
          planId = val.trim(url.query.planId || url.query.planid);
        }
      }

      const cfgOn = (Context.instance.uiMode.isConfiguratorOn ||
                     Context.instance.session.startInConfiguratorMode);
      const task = {
        cfgOn: cfgOn
      };

      const project = new Project();
      this.project = project;
      project.interrogateMap().then(() => {
        console.log("project",project)
        if (!project.hasIssues()) {
          if (planId) {
            return this._openPlan(task,project,planId);
          } else {
            return this._showOpenPlan(task,project);
          }
        } else {
          this._showProjectIssues(task,project);
        }
      }).then(() => {
        resolve();
      }).catch(ex =>  {
        console.warn("Error opening project",ex);
        this._handleOpenProjectError(task,project,ex);
        resolve();
      });

    });
    return promise;
  }

  _openPlan(task, project: Project, planId: string) {
    const promise = new Promise<void>((resolve,reject) => {
      if (Context.instance.spaceplanner.activePlan) {
        Context.instance.spaceplanner.activePlan.destroy();
      }
      const plan = new OfficePlan();
      const opener = new PlanOpener();
      Promise.resolve().then(result => {
        if (project.isVersioned) {
          return opener.openVersionedPlan(project,plan,planId);
        } else {
          return opener.openHostedPlan(project,plan,planId);
        }
      }).then(() => {
        return plan._init();
      }).then(() => {
        if (!plan.hasIssues()) {
          plan.wasOpened = true;
          Context.instance.spaceplanner.planId = planId;
          Context.instance.spaceplanner.activePlan = plan;
          Topic.publish(Topic.PlanOpened,{
            plan: plan,
            view: Context.instance.views.mapView
          });
          Topic.publish(Topic.CloseBackstage,{});
          console.log("plan.opened",plan)
          console.log("plan.supportsComment",plan.supportsComment());
          console.log("plan.supportsReview",plan.supportsReview());
          if (project.isVersioned) {
            this._checkReconcileOnOpen(project,plan);
          } else {
            this._checkReconcileOnOpenHosted(project,plan);
          }
        } else {
          this._showPlanIssues(task,project,plan);
        }
      }).then(() => {
        resolve();
      }).catch(ex => {
        console.error("Error opening plan",ex); // TODO
        this._handleOpenPlanError(task,project,ex);
        resolve();
      });
    });
    return promise;
  };

  reloadApp(appId: string, planId: string) {
    const lib = Context.instance.lib;
    const urlObj = lib.esri.urlUtils.urlToObject(window.location.href);
    let href = urlObj.path;
    if (href.indexOf("#") > 0) href = href.substring(0,href.indexOf("#"));
    href += "?appid=" + encodeURIComponent(appId);
    href += "&planid=" + encodeURIComponent(planId);
    if (urlObj.query && urlObj.query.edit === "true") {
      href += "&edit=true";
    }
    if (urlObj.query && urlObj.query.locale) {
      href += "&locale=" + encodeURIComponent(urlObj.query.locale);
    }
    window.location.href = href;
  }

  _showOpenPlan(task, project: Project) {
    const promise = new Promise<void>((resolve,reject) => {
      if (Context.instance.spaceplanner.activePlan) {
        Context.instance.spaceplanner.activePlan.destroy();
      }
      const plan = new OfficePlan();
      Context.instance.spaceplanner.activePlan = plan;
      plan._init().then(() => {
        if (!task.cfgOn) {
          Topic.publish(Topic.ShowBackstage,{
            forceRefresh: true,
            tabKey: "openPlan"
          });
        }
        resolve();
      }).catch(ex => {
        reject(ex);
      });
    });
    return promise;
  }

  _showPlanIssues(task,project,plan) {
    const i18n = Context.instance.i18n;
    const title = i18n.spaceplanner.issues.unableToOpenPlanTitle;
    ModalController.confirm({
      title: title,
      content: <Issues issues={plan.issues} />
    }).then(result => {
      this._showOpenPlan(task,project);
    });
  }

  _showProjectIssues(task,project) {
    const i18n = Context.instance.i18n;
    //const title = i18n.spaceplanner.issues.projectIssues;
    const title = i18n.spaceplanner.issues.requiredLayersMissing;
    ModalController.confirm({
      title: title,
      content: <Issues issues={project.issues} />
    }).then(result => {
      this._showOpenPlan(task,project);
    });
  }

}
